/**
 * `AIPromptStoreModel.ts` — the implementation of {@link IAIPromptStoreModel}.
 * See the interface for scope, for why publishing REPLACES rather than shadows,
 * and for why the document is per deployment.
 *
 * Shaped after `AppSettingsModel`: registered in the core IOC pass with no
 * transport, usable before one arrives (synchronous getters answer "nothing
 * published", which is the same state it is in while the first read is in
 * flight), and handed `auth` + `storage` further down via
 * {@link attachTransport}. It deliberately does NOT live inside
 * `app-settings.json`: prompt bodies are kilobytes each and that document is
 * republished whole on every settings write, so a toggle would rewrite every
 * prompt.
 */

import { Emitter } from "@core/events/emitter";
import type { IDebugModeModel } from "@models/ui/IDebugModeModel";
import type { IAuthModel } from "@models/auth/IAuthModel";
import type { IDeploymentConfigModel } from "@models/config/IDeploymentConfigModel";
import {
  agentPromptKey,
  AGENT_PROMPT_KINDS,
  parseAgentPromptsDocument,
  serializeAgentPromptsDocument,
  type AgentPromptEntryDto,
  type AgentPromptKind,
  type AgentPromptsDocumentDto,
} from "@models/data/agentPromptsDto";
import { runOncePerOrigin } from "@models/utils/runOncePerOrigin";
import type {
  AgentPromptRecord,
  AgentPromptWriteResult,
  IAIPromptStoreModel,
} from "./IAIPromptStoreModel";

/** The pre-cloud namespace: an index key plus one `Text.` key per file. */
const K_LEGACY_PREFIX = "Campfire.Dev.PromptOverride.";

export class AIPromptStoreModel implements IAIPromptStoreModel {
  readonly onChanged = new Emitter<void>();

  private m_doc: AgentPromptsDocumentDto = { version: 1, prompts: {} };
  private m_isLoaded = false;
  private m_lastError: string | null = null;
  private m_loadTask: Promise<void> | null = null;
  private m_auth: IAuthModel | null = null;
  private m_config: IDeploymentConfigModel | null = null;

  constructor(private readonly m_debug: IDebugModeModel) {
    runOncePerOrigin("prompt-overrides-to-app-settings", forgetLegacyLocalOverrides);
  }

  get isLoaded(): boolean {
    return this.m_isLoaded;
  }

  get lastError(): string | null {
    return this.m_lastError;
  }

  get hasPublishedPrompts(): boolean {
    return Object.keys(this.m_doc.prompts).length > 0;
  }

  /**
   * NOT gated on debug mode — that is the change this model exists for. A
   * published prompt is what the product runs; see the interface header.
   */
  getPromptText(kind: AgentPromptKind, fileName: string): string | null {
    const key = agentPromptKey(kind, fileName);
    if (!key) return null;
    // A blank body is refused on write and dropped on parse, so anything present
    // here is usable — no healing path to run.
    return this.m_doc.prompts[key]?.text ?? null;
  }

  isPublished(kind: AgentPromptKind, fileName: string): boolean {
    const key = agentPromptKey(kind, fileName);
    return key !== null && this.m_doc.prompts[key] !== undefined;
  }

  getPublishedAt(kind: AgentPromptKind, fileName: string): number | null {
    const key = agentPromptKey(kind, fileName);
    if (!key) return null;
    return this.m_doc.prompts[key]?.uploadedAt ?? null;
  }

  getPublishedBy(kind: AgentPromptKind, fileName: string): string | null {
    const key = agentPromptKey(kind, fileName);
    if (!key) return null;
    const by = this.m_doc.prompts[key]?.uploadedBy;
    return by?.trim() ? by : null;
  }

  /** See the interface. Order-independent, so a re-serialized document is equal. */
  get publishedSignature(): string {
    return Object.entries(this.m_doc.prompts)
      .map(([key, entry]) => `${key}@${entry.uploadedAt}`)
      .sort()
      .join("|");
  }

  listPublished(): ReadonlyArray<AgentPromptRecord> {
    const records: AgentPromptRecord[] = [];
    for (const [key, entry] of Object.entries(this.m_doc.prompts)) {
      const slash = key.indexOf("/");
      if (slash <= 0) continue;
      const kind = key.substring(0, slash) as AgentPromptKind;
      if (!AGENT_PROMPT_KINDS.includes(kind)) continue;
      records.push({
        kind,
        fileName: key.substring(slash + 1),
        at: entry.uploadedAt,
        by: entry.uploadedBy,
      });
    }
    return records.sort((a, b) => (a.kind + a.fileName).localeCompare(b.kind + b.fileName));
  }

  /**
   * @param auth used ONLY to stamp `uploadedBy` and to obtain the token a write
   * needs. The document itself is not the account's, so — unlike before this
   * moved off customer storage — there are no sign-in / sign-out subscriptions
   * here: signing out does not change which prompts this deployment runs, and
   * clearing them would have shown the login screen a different agent from the
   * one behind it.
   */
  attachTransport(auth: IAuthModel, config: IDeploymentConfigModel): void {
    this.m_auth = auth;
    this.m_config = config;
    void this.ensureLoadedAsync();
  }

  ensureLoadedAsync(): Promise<void> {
    if (this.m_loadTask) return this.m_loadTask;
    if (this.m_isLoaded) return Promise.resolve();
    return this.reloadAsync();
  }

  async publishAsync(
    kind: AgentPromptKind,
    fileName: string,
    text: string,
  ): Promise<AgentPromptWriteResult> {
    // The only affordance that publishes or resets a prompt is the debug dialog,
    // so nothing may publish from a build showing no developer surfaces. Note
    // this is a WRITE guard only — reads are ungated, which is the point of the
    // model (see the interface header).
    if (!this.m_debug.isEnabled) return { ok: false, error: "gated" };

    const key = agentPromptKey(kind, fileName);
    if (!key) return { ok: false, error: "blank" };
    // Blank is refused HERE rather than left to the catalog: an empty file makes
    // the loader reject the whole replacement before it parses anything, so every
    // later load would keep the previous live catalog until someone reset it.
    if (!text || !text.trim()) return { ok: false, error: "blank" };

    const entry: AgentPromptEntryDto = {
      text,
      uploadedAt: Date.now(),
      // `displayName`, matching how `AppSettingsModel` stamps `modifiedBy` — one
      // spelling of "who did this" across the shared documents.
      uploadedBy: this.m_auth?.currentUser?.displayName ?? "unknown",
    };
    const ok = await this.writeAsync((prompts) => ({ ...prompts, [key]: entry }));
    // A failed publish must NOT be swallowed the way a dropped boolean preference
    // can be — a silently discarded prompt edit reads as "I uploaded it and
    // nothing happened".
    return ok ? { ok: true } : { ok: false, error: "storage" };
  }

  async resetAsync(kind: AgentPromptKind, fileName: string): Promise<boolean> {
    const key = agentPromptKey(kind, fileName);
    if (!key || !this.m_doc.prompts[key]) return false;
    return this.writeAsync((prompts) => {
      const next = { ...prompts };
      delete next[key];
      return next;
    });
  }

  async resetAllAsync(): Promise<boolean> {
    if (!this.hasPublishedPrompts) return false;
    return this.writeAsync(() => ({}));
  }

  private reloadAsync(): Promise<void> {
    return (this.m_loadTask ??= this.loadAsync().finally(() => {
      this.m_loadTask = null;
    }));
  }

  private async loadAsync(): Promise<void> {
    const config = this.m_config;
    if (!config) return;

    try {
      // No sign-in gate: a read is unauthenticated, so the prompts this
      // deployment runs are known before anyone logs in.
      const json = await config.getAsync("prompts");
      // `null` is ABSENT — a deployment running entirely stock prompts. The
      // storage layer THROWS on a real failure, so this cannot latch "nothing
      // published" over prompts that exist.
      this.m_doc = parseAgentPromptsDocument(json);
      this.m_isLoaded = true;
      this.m_lastError = null;
    } catch (err) {
      // Keep whatever was cached and say so. NOT marked loaded, so the next
      // `ensureLoadedAsync` retries rather than latching the failure.
      this.m_lastError = errorText(err);
      console.warn("[AIPromptStoreModel] prompts document read failed:", err);
    }
    this.onChanged.emit();
  }

  /**
   * Read-modify-write against the deployment's document.
   *
   * The re-read is what keeps two people publishing different files from
   * clobbering each other, and what lets a client that has been open a while
   * publish without reverting someone else's newer prompt. A failed re-read
   * ABORTS: publishing a document built on a failed read would drop every other
   * published prompt on the deployment, which is worse than not saving.
   */
  private async writeAsync(
    mutate: (prompts: Record<string, AgentPromptEntryDto>) => Record<string, AgentPromptEntryDto>,
  ): Promise<boolean> {
    const config = this.m_config;
    if (!config) {
      this.m_lastError = "no transport";
      return false;
    }

    try {
      const remoteJson = await config.getAsync("prompts");
      const remote = parseAgentPromptsDocument(remoteJson);
      const next: AgentPromptsDocumentDto = {
        version: remote.version,
        prompts: mutate(remote.prompts),
      };
      // Rejects when the host refuses the token — writing is the one half of
      // this model that still needs a signed-in user.
      await config.putAsync("prompts", serializeAgentPromptsDocument(next));
      this.m_doc = next;
      this.m_isLoaded = true;
      this.m_lastError = null;
      this.onChanged.emit();
      return true;
    } catch (err) {
      this.m_lastError = errorText(err);
      console.warn("[AIPromptStoreModel] prompts document write failed:", err);
      this.onChanged.emit();
      return false;
    }
  }
}

function errorText(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/**
 * Drops the pre-cloud `localStorage` keys from any browser that still carries
 * them. Deletion, not migration — an edit made per-browser was never the
 * deployment's, and republishing one as the shared truth would overwrite
 * whatever the team is actually running.
 *
 * Run ONCE PER ORIGIN: the origin hosts several builds over one shared
 * `localStorage`. See `runOncePerOrigin`.
 */
function forgetLegacyLocalOverrides(): void {
  try {
    const stale: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key?.startsWith(K_LEGACY_PREFIX)) stale.push(key);
    }
    // Collected first: removing during the walk shifts every later index.
    for (const key of stale) window.localStorage.removeItem(key);
  } catch {
    // A browser refusing storage has nothing stored to clean up.
  }
}
